import React, { useState } from "react";
import PropTypes from "prop-types";
import SettingsItem from "../SettingsItem";
import TextInput from "shared/@forms/TextInput";
import Button, { ButtonPurpose, ButtonSize } from "shared/Button";
import styles from "./TagsItem.module.css";

const TagsItem = ({
  title,
  sections,
  locations,
  selectedLocations,
  onToggleLocation,
  onAddLocation,
  languages,
  selectedLanguages,
  onToggleLanguage,
  onAddLanguage,
  occupations,
  selectedOccupations,
  onToggleOccupation,
  onAddOccupation,
  keywords,
  onAddKeyword,
  hiddenTags,
  onAddHiddenTag,
  onTagClick,
}) => {
  const [addingSectionId, setAddingSectionId] = React.useState(null);
  const [addDraftBySectionId, setAddDraftBySectionId] = React.useState({});
  const [editingSectionId, setEditingSectionId] = React.useState(null);
  const [deletedTagsBySectionId, setDeletedTagsBySectionId] = React.useState({});
  const [hiddenDeletedTagsBySectionId, setHiddenDeletedTagsBySectionId] = React.useState({});
  const [savingTagSectionId, setSavingTagSectionId] = React.useState(null);


  const getPlaceholderText = (sectionTitle) => {
    const titleLower = String(sectionTitle || "").toLowerCase();
    if (titleLower.includes("location")) return "Add a Country";
    if (titleLower.includes("language")) return "Add a Language";
    if (titleLower.includes("occupation")) return "Add an Occupation";
    if (titleLower.includes("keyword")) return "Add a Tag";
    return "Add an Item";
  }

  const updateAddDraft = (sectionId, value) => {
    setAddDraftBySectionId((current) => ({
      ...current,
      [sectionId]: value,
    }));
  };

  const clearAddDraft = (sectionId) => {
    setAddDraftBySectionId((current) => {
      const next = { ...current };
      delete next[sectionId];
      return next;
    });
  };

  const toggleTagForDeletion = (sectionId, tag) => {
    setDeletedTagsBySectionId((current) => {
      const deletedTags = current[sectionId] || new Set();
      const newDeletedTags = new Set(deletedTags);
      if (newDeletedTags.has(tag)) {
        newDeletedTags.delete(tag);
      } else {
        newDeletedTags.add(tag);
      }
      return {
        ...current,
        [sectionId]: newDeletedTags,
      };
    });
  };

  const isTagMarkedForDeletion = (sectionId, tag) => {
    return deletedTagsBySectionId[sectionId]?.has(tag) || false;
  };

  const saveDeletedTags = async (section, sectionId) => {
    const deletedTags = deletedTagsBySectionId[sectionId];
    if (!deletedTags || deletedTags.size === 0) {
      setEditingSectionId(null);
      return;
    }

    setSavingTagSectionId(sectionId);
    try {
      for (const tag of deletedTags) {
        if (section.onDelete) {
          await Promise.resolve(section.onDelete(tag));
        }
      }
      
      // After successful deletion, hide the tags by adding to hiddenDeletedTagsBySectionId
      setHiddenDeletedTagsBySectionId((current) => {
        const next = { ...current };
        next[sectionId] = deletedTags;
        return next;
      });

      // Only clear the deletion state if all deletions succeeded
      setEditingSectionId(null);
      setDeletedTagsBySectionId((current) => {
        const next = { ...current };
        delete next[sectionId];
        return next;
      });
    } catch (error) {
      console.error("Error deleting tags:", error);
      // Keep the deletion state so user can try again or cancel
      throw error;
    } finally {
      setSavingTagSectionId(null);
    }
  };

  const cancelDelete = (sectionId) => {
    setEditingSectionId(null);
    setDeletedTagsBySectionId((current) => {
      const next = { ...current };
      delete next[sectionId];
      return next;
    });
  };

  const renderAddComposer = (section, sectionId) => {
    if (!section?.onAdd) {
      return null;
    }

    const isAdding = addingSectionId === sectionId;
    const draftValue = addDraftBySectionId[sectionId] || "";

    const submitAdd = async () => {
      const normalized = String(draftValue).trim();
      if (!normalized) {
        return;
      }

      const addResult = await Promise.resolve(section.onAdd(normalized));
      if (addResult === false) {
        return;
      }

      clearAddDraft(sectionId);
      setAddingSectionId(null);
    };

    if (!isAdding) {
      return (
        <button
          type="button"
          className={styles.addInlineButton}
          onClick={() => setAddingSectionId(sectionId)}
        >
          + Add
        </button>
      );
    }

    return (
      <div className={styles.addComposerRow}>
        <input
          type="text"
          className={styles.addComposerInput}
          value={draftValue}
          placeholder={getPlaceholderText(section?.title)}
          onChange={(event) => updateAddDraft(sectionId, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void submitAdd();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              clearAddDraft(sectionId);
              setAddingSectionId(null);
            }
          }}
        />

         <Button
          type="button"
          className={styles.cancelButton}
          onClick={() => {
            clearAddDraft(sectionId);
            setAddingSectionId(null);
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={styles.addButton}
          onClick={() => {
            void submitAdd();
          }}
        >
          Add
        </Button>
       
      </div>
    );
  };

  const renderCheckboxList = (options, selectedMap, onToggle) => (
    <div className={styles.checkboxGrid}>
      {options.map((option) => {
        const checked = !!selectedMap?.[option];
        return (
          <label key={option} className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={checked}
              onChange={() => onToggle?.(option, !checked)}
            />
            <span>{option}</span>
          </label>
        );
      })}
    </div>
  );

 

  const renderDynamicSections = () => {
    if (!Array.isArray(sections) || sections.length === 0) {
      return null;
    }

    return sections.map((section, index) => {
      const sectionId = section.id || section.title || String(index);
      const componentType = String(section.componentType || "").toUpperCase();
      const isChipSection = componentType === "CHIP_SELECTOR_MULTI";
      const selectedValues = Array.isArray(section.selectedValues)
        ? section.selectedValues
        : [];
      const selectedSet = new Set(selectedValues);
      const availableValues = (section.options || []).filter(
        (tag) => !selectedSet.has(tag),
      );
      const selectedMap = selectedValues.reduce((acc, value) => {
        acc[value] = true;
        return acc;
      }, {});

      return (
        <React.Fragment key={sectionId}>
          <div className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <div className={styles.sectionTitle}>{section.title}</div>
              {isChipSection && editingSectionId !== sectionId ? (
                <Button
                  type="button"
                  size={ButtonSize.small}
                  purpose={ButtonPurpose.primary}
                  onClick={() => setEditingSectionId(sectionId)}
                  className={styles.editButton}
                >
                  Edit
                </Button>
              ) : editingSectionId === sectionId ? (
                <div className={styles.headerButtonGroup}>
                  <Button
                    type="button"
                    onClick={() => cancelDelete(sectionId)}
                    className={styles.cancelButton}
                    disabled={savingTagSectionId === sectionId}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className={styles.saveButton}
                    onClick={() => saveDeletedTags(section, sectionId)}
                    disabled={savingTagSectionId === sectionId}
                  >
                    {savingTagSectionId === sectionId ? "Saving..." : "Save"}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className={styles.divider} />

            {isChipSection ? (
              <>
                <div className={styles.selectedTagContainer}>
                  <div className={styles.selectedTagGrid}>
                    {selectedValues.length > 0 ? (
                      selectedValues
                        .filter((tag) => !hiddenDeletedTagsBySectionId[sectionId]?.has(tag))
                        .map((tag) => {
                          const isMarkedForDeletion = isTagMarkedForDeletion(sectionId, tag);
                          return (
                          <div key={tag} className={styles.tagWithDeleteWrapper}>
                            <button
                              type="button"
                              className={`${styles.tagPill} ${styles.tagPillActive} ${isMarkedForDeletion ? styles.tagMarkedForDeletion : ''}`}
                              onClick={() => {
                                if (editingSectionId === sectionId) {
                                  toggleTagForDeletion(sectionId, tag);
                                } else {
                                  section.onToggle?.(tag, false);
                                }
                              }}
                            >
                              {tag}
                            </button>
                            {editingSectionId === sectionId && (
                              <button
                                type="button"
                                className={styles.deleteXButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTagForDeletion(sectionId, tag);
                                }}
                                title="Mark for deletion"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        );
                        })
                    ) : (
                      <div style={{ color: "#999", fontSize: "12px" }}>No selected tags</div>
                    )}
                  </div>
                </div>

                <div className={styles.availableTagContainer}>
                  <div className={styles.availableTagGrid}>
                    {availableValues
                      .filter((tag) => !hiddenDeletedTagsBySectionId[sectionId]?.has(tag))
                      .map((tag) => {
                        const isMarkedForDeletion = isTagMarkedForDeletion(sectionId, tag);
                        return (
                        <div key={tag} className={styles.tagWithDeleteWrapper}>
                          <button
                            type="button"
                            className={`${styles.tagPill} ${styles.tagPillInactive} ${isMarkedForDeletion ? styles.tagMarkedForDeletion : ''}`}
                            onClick={() => {
                              if (editingSectionId === sectionId) {
                                toggleTagForDeletion(sectionId, tag);
                              } else {
                                section.onToggle?.(tag, true);
                              }
                            }}
                          >
                            {tag}
                          </button>
                          {editingSectionId === sectionId && (
                            <button
                              type="button"
                              className={styles.deleteXButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTagForDeletion(sectionId, tag);
                              }}
                              title="Mark for deletion"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                      })}

                    {renderAddComposer(section, sectionId)}
                  </div>
                </div>
              </>
            ) : (
              renderCheckboxList(section.options || [], selectedMap, (value) =>
                section.onToggle?.(value, !selectedMap?.[value]),
              )
            )}

            {!isChipSection && (
              <div className={styles.addButtonRow}>
                {renderAddComposer(section, sectionId)}
              </div>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  const dynamicSections = renderDynamicSections();

  if (!dynamicSections) {
    return null;
  }

  return <SettingsItem title={title}>{dynamicSections}</SettingsItem>;
};

TagsItem.propTypes = {
  title: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      componentType: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string),
      selectedValues: PropTypes.arrayOf(PropTypes.string),
      onToggle: PropTypes.func,
      onAdd: PropTypes.func,
    }),
  ),
  locations: PropTypes.arrayOf(PropTypes.string),
  selectedLocations: PropTypes.object,
  onToggleLocation: PropTypes.func,
  onAddLocation: PropTypes.func,
  languages: PropTypes.arrayOf(PropTypes.string),
  selectedLanguages: PropTypes.object,
  onToggleLanguage: PropTypes.func,
  onAddLanguage: PropTypes.func,
  occupations: PropTypes.arrayOf(PropTypes.string),
  selectedOccupations: PropTypes.object,
  onToggleOccupation: PropTypes.func,
  onAddOccupation: PropTypes.func,
  keywords: PropTypes.arrayOf(PropTypes.string),
  onAddKeyword: PropTypes.func,
  hiddenTags: PropTypes.arrayOf(PropTypes.string),
  onAddHiddenTag: PropTypes.func,
  onTagClick: PropTypes.func,
};

TagsItem.defaultProps = {
  title: "Tags, etc",
  sections: [],
  locations: [],
  selectedLocations: {},
  onToggleLocation: undefined,
  onAddLocation: undefined,
  languages: [],
  selectedLanguages: {},
  onToggleLanguage: undefined,
  onAddLanguage: undefined,
  occupations: [],
  selectedOccupations: {},
  onToggleOccupation: undefined,
  onAddOccupation: undefined,
  keywords: [],
  onAddKeyword: undefined,
  hiddenTags: [],
  onAddHiddenTag: undefined,
  onTagClick: undefined,
};

export default TagsItem;
